import { useCallback, useEffect, useRef, useContext } from 'react';
import { AutosaveContext } from './autosave-context';
import { useNavigation } from 'react-router';
import { SentryClient } from '~/lib/sentry.client';

type UseAutosaveProps = {
  onAutosave?: (formRef: HTMLFormElement) => Promise<void>;
  autosaveDelayMs?: number;
};

export const AUTOSAVE_INTENT = {
  key: '_action',
  value: 'autosave',
};

export function useAutosave({ onAutosave, autosaveDelayMs = 5000 }: UseAutosaveProps = {}) {
  const { setAutosaveStatus, autosaveStatus, setHasChanges, markAsSaved, resetAutosave } =
    useContext(AutosaveContext);
  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';

  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const performAutosave = useCallback(async () => {
    if (!onAutosave) return;

    setAutosaveStatus(current => {
      if (current.isAutosaving || isSubmitting || !current.hasChanges) {
        return current;
      }
      return {
        ...current,
        isAutosaving: true,
      };
    });

    try {
      if (formRef.current) {
        await onAutosave(formRef.current);
        markAsSaved();
      }
    } catch (error) {
      SentryClient.captureException(error, {
        extra: { message: 'Autosave failed' },
      });

      setAutosaveStatus(current => ({
        ...current,
        isAutosaving: false,
      }));
    }
  }, [onAutosave, setAutosaveStatus, isSubmitting, markAsSaved]);

  // Set up autosave timer
  useEffect(() => {
    if (!onAutosave) return;

    const startAutosaveTimer = () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
      autosaveTimerRef.current = setTimeout(performAutosave, autosaveDelayMs);
    };

    // Start timer if there are changes
    if (autosaveStatus.hasChanges) {
      startAutosaveTimer();
    }

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [onAutosave, performAutosave, autosaveDelayMs, autosaveStatus.hasChanges]);

  return {
    formRef,
    autosaveStatus,
    setHasChanges,
    markAsSaved,
    resetAutosave,
  };
}
